import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IoArrowBack, IoClose } from 'react-icons/io5';
import { useBooks } from '../context/BooksContext';
import { useUser } from '../context/UserContext';
import ImageUpload from '../components/ImageUpload';

const GENRES = [
  { id: 'romance', label: '💕 Romance' },
  { id: 'fantasy', label: '🗡️ Fantasy' },
  { id: 'thriller', label: '🔍 Thriller' },
  { id: 'scifi', label: '🚀 Sci-Fi' },
  { id: 'drama', label: '🎭 Drame' },
  { id: 'horror', label: '👻 Horreur' },
  { id: 'humor', label: '😂 Humour' },
  { id: 'poetry', label: '✨ Poésie' },
  { id: 'adventure', label: '🧭 Aventure' },
  { id: 'other', label: '📖 Autre' },
];

export default function WriteBook() {
  const navigate = useNavigate();
  const { addBook } = useBooks();
  const { user } = useUser();
  const [form, setForm] = useState({
    title: '', synopsis: '', genre: '', tags: [],
    coverImage: null, authorNote: '',
  });
  const [tagInput, setTagInput] = useState('');
  const [error, setError] = useState('');

  const addTag = (value) => {
    const tag = value.trim().toLowerCase();
    if (tag && !form.tags.includes(tag) && form.tags.length < 10) {
      setForm({ ...form, tags: [...form.tags, tag] });
    }
    setTagInput('');
  };

  const handleTagKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(tagInput);
    }
    if (e.key === 'Backspace' && !tagInput && form.tags.length > 0) {
      setForm({ ...form, tags: form.tags.slice(0, -1) });
    }
  };

  const removeTag = (index) => {
    setForm({ ...form, tags: form.tags.filter((_, i) => i !== index) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.title) { setError('Le titre est requis'); return; }
    if (!form.synopsis) { setError('Le synopsis est requis'); return; }
    if (!form.genre) { setError('Choisis un genre'); return; }
if (!form.coverImage) { setError('Une image de couverture est obligatoire (max 10 Mo, jpeg/png)'); return; }


    try {
      const newBook = await addBook({
        title: form.title,
        author: user.name,
        authorId: user.authorId,
        synopsis: form.synopsis,
        genre: form.genre,
        tags: form.tags,
        coverImage: form.coverImage,
        coverGradient: 'linear-gradient(135deg, #2c2a27, #1a1815)',
        authorNote: form.authorNote || '',
        status: 'draft',
      });

      if (newBook && newBook.id) {
        navigate('/author/write/' + newBook.id + '/chapter');
      } else {
        navigate('/author');
      }
    } catch (err) {
      setError('Erreur lors de la création. Réessaie.');
    }
  };

  return (
    <div className="page">
      <button className="btn btn-ghost" onClick={() => navigate('/author')} style={{ marginBottom: 16 }}>
        <IoArrowBack /> Retour
      </button>

      <h1 className="page-title">Nouveau livre</h1>
      <p className="page-subtitle" style={{ marginBottom: 24 }}>Donne vie à ton histoire</p>

      {error && (
        <div style={{
          background: '#fce4ec', color: '#c62828', padding: '10px 14px',
          borderRadius: 10, marginBottom: 16, fontSize: 13, fontWeight: 500,
        }}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="input-group">
<label>Couverture (obligatoire — max 10 Mo, jpeg/png)</label>
          <ImageUpload
            onImageUploaded={(url) => setForm({ ...form, coverImage: url })}
            currentImage={form.coverImage}
          />
        </div>

        <div className="input-group">
          <label>Titre</label>
          <input className="input" placeholder="Le titre de ton livre" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
        </div>

        <div className="input-group">
          <label>Synopsis</label>
          <textarea className="input" style={{ minHeight: 100, resize: 'vertical' }} placeholder="De quoi parle ton histoire ?" value={form.synopsis} onChange={e => setForm({ ...form, synopsis: e.target.value })} />
        </div>

        <div className="input-group">
          <label>Genre</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {GENRES.map(g => (
              <button type="button" key={g.id}
                className={'btn ' + (form.genre === g.id ? 'btn-primary' : 'btn-secondary')}
                style={{ padding: '8px 14px', fontSize: 13 }}
                onClick={() => setForm({ ...form, genre: g.id })}
              >{g.label}</button>
            ))}
          </div>
        </div>

        <div className="input-group">
          <label>Note d'auteur / Dédicace (optionnel)</label>
          <textarea
            className="input"
            style={{ minHeight: 80, resize: 'vertical' }}
            placeholder="Un mot pour tes lecteurs, une dédicace..."
            value={form.authorNote}
            onChange={e => setForm({ ...form, authorNote: e.target.value })}
            maxLength={500}
          />
        </div>

        <div className="input-group">
          <label>Tags ({form.tags.length}/10) — tape un mot puis Entrée ou virgule</label>
          <div style={{
            display: 'flex', flexWrap: 'wrap', gap: 6, padding: '8px 12px',
            border: '2px solid var(--parchment-dark)', borderRadius: 12, minHeight: 44, alignItems: 'center',
            background: 'white',
          }}>
            {form.tags.map((tag, i) => (
              <span key={i} style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '4px 10px', borderRadius: 8, background: 'var(--amber-light)',
                fontSize: 13, fontWeight: 500, color: 'var(--ink)',
              }}>
                #{tag}
                <IoClose style={{ cursor: 'pointer', opacity: 0.5, fontSize: 14 }} onClick={() => removeTag(i)} />
              </span>
            ))}
            <input
              style={{
                border: 'none', outline: 'none', flex: 1, minWidth: 100,
                fontSize: 13, fontFamily: 'var(--font-body)', background: 'transparent',
              }}
              placeholder={form.tags.length === 0 ? 'amour, Paris, mystère...' : ''}
              value={tagInput}
              onChange={e => setTagInput(e.target.value)}
              onKeyDown={handleTagKeyDown}
              onBlur={() => { if (tagInput.trim()) addTag(tagInput); }}
            />
          </div>
        </div>

        <button type="submit" className="btn btn-primary btn-full" style={{ marginTop: 8, padding: 16, fontSize: 16 }}>
          Créer le livre
        </button>
      </form>
    </div>
  );
}